import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { contactService } from '../services/api';
import { BR_STATES, isBrazil } from '../data/brazilStates';
import { useBrazilCities } from '../hooks/useBrazilCities';
import BirthTimeSelect, { isCompleteTime, isPartialTime } from './BirthTimeSelect';

interface Contact {
  id: number;
  name: string;
  relationship_type: string | null;
  birth_date: string;
  birth_time: string | null;
  birth_city: string | null;
  birth_state: string | null;
  birth_country: string | null;
  notes: string | null;
}

const EMPTY_FORM = {
  name: '',
  relationship_type: '',
  birth_date: '',
  birth_time: '',
  birth_city: '',
  birth_state: '',
  birth_country: 'Brasil',
  notes: '',
};

const fmtBirth = (birthDate: string | null, birthTime: string | null): string => {
  const iso = birthDate?.slice(0, 10);
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  const dateStr = isNaN(d.getTime()) ? iso : d.toLocaleDateString('pt-BR');
  return birthTime ? `${dateStr} às ${birthTime.slice(0, 5)}` : dateStr;
};

const REL_OPTIONS = [
  { value: 'romantico', label: '❤️ Romântico' },
  { value: 'amizade', label: '🤝 Amizade' },
  { value: 'familiar', label: '👨‍👩‍👧 Familiar' },
  { value: 'profissional', label: '💼 Profissional' },
];

const REL_LABEL: Record<string, string> = {
  romantico: '❤️ Romântico',
  amizade: '🤝 Amizade',
  familiar: '👨‍👩‍👧 Familiar',
  profissional: '💼 Profissional',
};

const inputClass =
  'w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-purple-400 transition-colors';
const selectClass =
  'w-full px-4 py-3 rounded-xl border border-white/10 text-white focus:outline-none focus:border-purple-400 transition-colors';
const labelClass = 'block text-sm text-white/60 mb-1';

const ContactsTab: React.FC = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    contactService.list().then(setContacts).catch(() => setContacts([])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setMsg(null);
    setShowForm(true);
  };

  const openEdit = (c: Contact) => {
    setForm({
      name: c.name,
      relationship_type: c.relationship_type || '',
      birth_date: c.birth_date ? c.birth_date.slice(0, 10) : '',
      birth_time: c.birth_time ? c.birth_time.slice(0, 5) : '',
      birth_city: c.birth_city || '',
      birth_state: c.birth_state || '',
      birth_country: c.birth_country || 'Brasil',
      notes: c.notes || '',
    });
    setEditingId(c.id);
    setMsg(null);
    setShowForm(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Select de cidade por UF (evita erro de digitação que atrapalha a geocodificação
  // do mapa astral) — só disponível pra Brasil; fora disso cai pra texto livre.
  const brazilContact = isBrazil(form.birth_country);
  const { cities: brCitiesContact, loading: citiesLoadingContact, failed: citiesFailedContact } = useBrazilCities(
    brazilContact ? form.birth_state : null
  );
  const showCitySelectContact = brazilContact && !!form.birth_state && !citiesFailedContact;
  const handleContactStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, birth_state: e.target.value, birth_city: '' }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPartialTime(form.birth_time)) {
      setMsg({ type: 'error', text: 'Selecione a hora e o minuto, ou deixe os dois em branco.' });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      const payload: any = { ...form };
      if (!isCompleteTime(payload.birth_time)) delete payload.birth_time;
      if (!payload.relationship_type) delete payload.relationship_type;

      if (editingId !== null) {
        await contactService.update(editingId, payload);
      } else {
        await contactService.create(payload);
      }
      setMsg({ type: 'success', text: editingId !== null ? 'Conexão atualizada!' : 'Conexão adicionada!' });
      setShowForm(false);
      setEditingId(null);
      load();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setMsg({ type: 'error', text: Array.isArray(detail) ? detail.map((d: any) => d.msg).join(' | ') : (detail || 'Erro ao salvar') });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await contactService.remove(id);
      setDeleteConfirm(null);
      load();
    } catch {
      setMsg({ type: 'error', text: 'Erro ao remover conexão' });
    }
  };

  const cardStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '1rem',
    padding: '1.5rem',
  };

  return (
    <div>
      {msg && !showForm && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${msg.type === 'success' ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-red-500/20 text-red-300 border border-red-500/30'}`}>
          {msg.text}
        </div>
      )}

      {!showForm ? (
        <>
          <div className="flex items-center justify-between mb-6">
            <p className="text-white/50 text-sm">Adicione pessoas para analisar a sinastria com você.</p>
            <button
              onClick={openNew}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all"
              style={{ background: 'linear-gradient(to right, #7c3aed, #8b5cf6)' }}
            >
              + Nova conexão
            </button>
          </div>

          {loading ? (
            <p className="text-white/40 text-sm text-center py-8">Carregando...</p>
          ) : contacts.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-3">🔮</div>
              <p className="text-white/40 text-sm">Nenhuma conexão cadastrada ainda.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {contacts.map(c => (
                <div key={c.id} style={cardStyle} className="flex flex-col gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1 min-w-0">
                      <span className="font-semibold text-white truncate">{c.name}</span>
                      {c.relationship_type && (
                        <span className="text-xs text-purple-300 bg-purple-500/15 px-2 py-0.5 rounded-full flex-shrink-0">
                          {REL_LABEL[c.relationship_type] || c.relationship_type}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-white/40">
                      {fmtBirth(c.birth_date, c.birth_time)}{c.birth_city ? ` · ${c.birth_city}` : ''}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => navigate(`/sinastria/${c.id}`)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all hover:opacity-90"
                      style={{ background: 'linear-gradient(to right, #c026d3, #8b5cf6)' }}
                      title="Ver sinastria"
                    >
                      ✨ Sinastria
                    </button>
                    <button
                      onClick={() => navigate(`/composto/${c.id}`)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all hover:opacity-90"
                      style={{ background: 'linear-gradient(to right, #7c3aed, #4f46e5)' }}
                      title="Ver Mapa Composto"
                    >
                      🌗 Composto
                    </button>
                    <button
                      onClick={() => navigate(`/davison/${c.id}`)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all hover:opacity-90"
                      style={{ background: 'linear-gradient(to right, #0ea5e9, #6366f1)' }}
                      title="Ver Mapa de Davison"
                    >
                      🕊️ Davison
                    </button>
                    <button
                      onClick={() => openEdit(c)}
                      className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors text-sm"
                      title="Editar"
                    >
                      ✏️
                    </button>
                    {deleteConfirm === c.id ? (
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleDelete(c.id)} className="px-2 py-1 rounded text-xs bg-red-500/80 text-white">Confirmar</button>
                        <button onClick={() => setDeleteConfirm(null)} className="px-2 py-1 rounded text-xs bg-white/10 text-white">Cancelar</button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(c.id)}
                        className="p-2 rounded-lg text-white/40 hover:text-red-400 hover:bg-white/10 transition-colors text-sm"
                        title="Remover"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div style={cardStyle}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white">
              {editingId !== null ? 'Editar conexão' : 'Nova conexão'}
            </h3>
            <button onClick={() => { setShowForm(false); setMsg(null); }} className="text-white/40 hover:text-white text-sm">
              ← Voltar
            </button>
          </div>

          {msg && (
            <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${msg.type === 'success' ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-red-500/20 text-red-300 border border-red-500/30'}`}>
              {msg.text}
            </div>
          )}

          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Nome completo *</label>
                <input type="text" name="name" value={form.name} onChange={handleChange} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Tipo de relação</label>
                <select name="relationship_type" value={form.relationship_type} onChange={handleChange} className={selectClass} style={{ background: 'rgba(255,255,255,0.05)' }}>
                  <option value="" style={{ background: '#1a1030' }}>Não informado</option>
                  {REL_OPTIONS.map(o => (
                    <option key={o.value} value={o.value} style={{ background: '#1a1030' }}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Data de nascimento *</label>
                <input type="date" name="birth_date" value={form.birth_date} onChange={handleChange} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Horário de nascimento</label>
                <BirthTimeSelect
                  value={form.birth_time}
                  onChange={(v) => setForm(prev => ({ ...prev, birth_time: v }))}
                  selectClassName={selectClass}
                  selectStyle={{ background: 'rgba(255,255,255,0.05)' }}
                  optionStyle={{ background: '#1a1030' }}
                  invalid={isPartialTime(form.birth_time)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Estado {brazilContact ? '(UF)' : ''}</label>
                {brazilContact ? (
                  <select name="birth_state" value={form.birth_state} onChange={handleContactStateChange} className={selectClass} style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <option value="" style={{ background: '#1a1030' }}>Selecione o estado</option>
                    {BR_STATES.map(s => (
                      <option key={s.uf} value={s.uf} style={{ background: '#1a1030' }}>{s.uf} — {s.name}</option>
                    ))}
                  </select>
                ) : (
                  <input type="text" name="birth_state" value={form.birth_state} onChange={handleChange} className={inputClass} placeholder="Estado/província" />
                )}
              </div>
              <div>
                <label className={labelClass}>Cidade de nascimento</label>
                {showCitySelectContact ? (
                  <select name="birth_city" value={form.birth_city} onChange={handleChange} disabled={citiesLoadingContact} className={selectClass} style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <option value="" style={{ background: '#1a1030' }}>
                      {citiesLoadingContact ? 'Carregando cidades…' : 'Selecione a cidade'}
                    </option>
                    {brCitiesContact.map(c => (
                      <option key={c} value={c} style={{ background: '#1a1030' }}>{c}</option>
                    ))}
                  </select>
                ) : (
                  <>
                    <input
                      type="text"
                      name="birth_city"
                      value={form.birth_city}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder={brazilContact && !form.birth_state ? 'Selecione o estado ao lado primeiro' : undefined}
                      disabled={brazilContact && !form.birth_state}
                    />
                    {citiesFailedContact && (
                      <p className="text-amber-300 text-xs mt-1">Não foi possível carregar a lista de cidades agora — digite manualmente.</p>
                    )}
                  </>
                )}
              </div>
            </div>

            <div>
              <label className={labelClass}>País</label>
              <input type="text" name="birth_country" value={form.birth_country} onChange={handleChange} className={inputClass} />
            </div>

            <div>
              <label className={labelClass}>Observações</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={2}
                className={inputClass + ' resize-none'}
                placeholder="Ex: parceiro(a), melhor amigo(a)..."
              />
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full py-3 rounded-xl font-semibold text-white transition-all"
              style={{ background: saving ? 'rgba(139,92,246,0.4)' : 'linear-gradient(to right, #7c3aed, #8b5cf6)' }}
            >
              {saving ? 'Salvando...' : editingId !== null ? 'Salvar alterações' : 'Adicionar conexão'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ContactsTab;
